import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { CompleteKundali, PlanetName, Language } from '../types/kundali';
import {
  analyzeGemstoneSuitability,
  OBJECTIVES,
  GEMSTONE_DETAILS,
  JYOTISHA_SOURCES,
  GemstoneSuitabilityReport,
  SuitabilityLevel,
  VEDIC_UPAYAS,
  VedicUpayaDetail,
  calculateGemstoneDateMuhurta,
  GemstoneTargetDateAnalysis,
  calculateBirthTimeSensitivity,
  QUALITY_AFFORDABILITY_GUIDANCE,
  generateScenarioComparisons,
  evaluateNoInterventionScenario,
  BirthTimeSensitivityAudit,
  ScenarioComparison,
  generateGemstoneReport,
  GemstoneReport,
  generateOptionComparisons,
  OptionComparisonItem,
  createAcharyaOverride,
  AcharyaOverrideRecord,
  submitGemstoneFeedback,
  GemstoneFeedbackRecord,
  GEMSTONE_ENGINE_VERSION,
  buildOfflineGemstoneConsultFallback
} from '../utils/gemstoneAdvisorEngine';
import { findBestMuhurtas } from '../utils/muhurtaEngine';
import { NAKSHATRAS } from '../utils/vedicEngine';
import { MuhurtaAnalysis, MuhurtaSearchParams } from '../types/muhurta';
import {
  Sparkles,
  Shield,
  Award,
  Flame,
  Check,
  AlertTriangle,
  Clock,
  BookOpen,
  Compass,
  HelpCircle,
  Info,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  FileText,
  Gem,
  Send,
  Loader2,
  Calendar,
  Printer,
  Download,
  HeartHandshake,
  CheckCircle2,
  X,
  ExternalLink,
  RefreshCw,
  ArrowRight,
  Target,
  Layers
} from 'lucide-react';
import { VedicGemologyPerspectivesView } from './VedicGemologyPerspectivesView';
import { JewelryConflictChecker } from './JewelryConflictChecker';

interface GemstoneAdvisorProps {
  kundali: CompleteKundali;
  lang?: Language;
}

const PLANET_MUHURTA_MILESTONE_MAP: Record<PlanetName, string> = {
  Sun: 'ruby_manik',
  Moon: 'pearl_moti',
  Mars: 'coral_moonga',
  Mercury: 'emerald_panna',
  Jupiter: 'yellow_sapphire_pukhraj',
  Venus: 'diamond_opal_heera',
  Saturn: 'blue_sapphire_neelam',
  Rahu: 'hessonite_gomed',
  Ketu: 'cats_eye_lehsunia',
  Ascendant: 'ruby_manik'
};

export const GemstoneAdvisor: React.FC<GemstoneAdvisorProps> = ({ kundali, lang = 'en' }) => {
  const [masterTab, setMasterTab] = useState<'perspectives' | 'conflict_checker' | 'objectives'>('perspectives');
  const [selectedObjective, setSelectedObjective] = useState<string>('general');
  const [viewMode, setViewMode] = useState<'basic' | 'advanced'>('advanced');
  const [gemstoneInMind, setGemstoneInMind] = useState<boolean>(false);
  const [selectedPlanetInMind, setSelectedPlanetInMind] = useState<PlanetName>('Saturn');
  const [selectedReportPlanet, setSelectedReportPlanet] = useState<PlanetName | null>(null);
  
  // Active sub-tab inside the selected report card
  const [reportSubTab, setReportSubTab] = useState<'factors' | 'muhurta' | 'upayas'>('factors');

  // Printable prescription modal state
  const [showPrescriptionModal, setShowPrescriptionModal] = useState<boolean>(false);

  // Custom interactive AI questions state
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiAnswer, setAiAnswer] = useState<string | null>(null);
  const [isAiLoading, setIsAiLoading] = useState(false);

  const jatakaNakshatra = kundali.panchang?.nakshatra || kundali.planets?.find(p => p.name === 'Moon')?.nakshatra || 'Ashwini';

  // Calculate the live gemstone analysis matrix dynamically
  const suitabilityReports = useMemo(() => {
    return analyzeGemstoneSuitability(kundali, selectedObjective, gemstoneInMind ? selectedPlanetInMind : undefined);
  }, [kundali, selectedObjective, gemstoneInMind, selectedPlanetInMind]);

  // If no report is selected, default to the top recommended planet
  const activeReport = useMemo(() => {
    if (selectedReportPlanet) {
      return suitabilityReports.find(r => r.planet === selectedReportPlanet) || suitabilityReports[0];
    }
    return suitabilityReports[0] || null;
  }, [suitabilityReports, selectedReportPlanet]);

  // Birth-time cuspal sensitivity audit
  const birthSensitivity = useMemo<BirthTimeSensitivityAudit>(() => {
    return calculateBirthTimeSensitivity(kundali);
  }, [kundali]);

  // Comparative scenarios across natal, active dasha, gochar, and no-intervention baseline
  const scenarioComparisons = useMemo<ScenarioComparison[]>(() => {
    return generateScenarioComparisons(suitabilityReports, kundali);
  }, [suitabilityReports, kundali]);

  // Evaluation of no-intervention scenario
  const noInterventionEval = useMemo(() => {
    return evaluateNoInterventionScenario(suitabilityReports);
  }, [suitabilityReports]);

  // Color helper for badges
  const getSuitabilityColor = (level: SuitabilityLevel) => {
    switch (level) {
      case 'STRONGLY SUITABLE':
        return {
          bg: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-700',
          badge: 'bg-emerald-500 text-white',
          text: 'text-emerald-700'
        };
      case 'SUITABLE':
        return {
          bg: 'bg-teal-500/10 border-teal-500/20 text-teal-700',
          badge: 'bg-teal-600 text-white',
          text: 'text-teal-700'
        };
      case 'CONDITIONALLY SUITABLE':
        return {
          bg: 'bg-amber-500/10 border-amber-500/20 text-amber-800',
          badge: 'bg-amber-600 text-white',
          text: 'text-amber-800'
        };
      case 'CAUTION':
        return {
          bg: 'bg-orange-500/10 border-orange-500/20 text-orange-700',
          badge: 'bg-orange-500 text-white',
          text: 'text-orange-700'
        };
      case 'NOT RECOMMENDED':
        return {
          bg: 'bg-rose-500/10 border-rose-500/20 text-rose-700',
          badge: 'bg-rose-500 text-white',
          text: 'text-rose-700'
        };
    }
  };

  // Option comparison items across eligible gemstones and substitutes (REQ-17)
  const optionComparisons = useMemo(() => {
    return generateOptionComparisons(suitabilityReports);
  }, [suitabilityReports]);

  // Acharya override state (REQ-18)
  const [showAcharyaOverrideModal, setShowAcharyaOverrideModal] = useState<boolean>(false);
  const [acharyaOverrides, setAcharyaOverrides] = useState<AcharyaOverrideRecord[]>([]);
  const [overrideSuitability, setOverrideSuitability] = useState<SuitabilityLevel>('SUITABLE');
  const [overrideScore, setOverrideScore] = useState<number>(75);
  const [overrideRationale, setOverrideRationale] = useState<string>('');
  const [overrideSources, setOverrideSources] = useState<string>('Brihat Parashara Hora Shastra, Ch. 34');

  // Feedback state (REQ-19)
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false);
  const [feedbackRating, setFeedbackRating] = useState<1 | 2 | 3 | 4 | 5>(5);
  const [feedbackCategory, setFeedbackCategory] = useState<GemstoneFeedbackRecord['category']>('ACCURACY');
  const [feedbackText, setFeedbackText] = useState<string>('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState<boolean>(false);

  // In-memory cache for calculated Muhurta windows (keyed by planet_nakshatra_rashi)
  const [muhurtaCache, setMuhurtaCache] = useState<Record<string, MuhurtaAnalysis[]>>({});
  const [muhurtaWindows, setMuhurtaWindows] = useState<MuhurtaAnalysis[]>([]);
  const [isLoadingMuhurta, setIsLoadingMuhurta] = useState<boolean>(false);

  // Generated printable / exportable report (REQ-09)
  const printableReport = useMemo(() => {
    if (!activeReport) return null;
    return generateGemstoneReport(kundali, activeReport, muhurtaWindows);
  }, [kundali, activeReport, muhurtaWindows]);

  // Muhurta selection mode: 'calendar' (35-day top ranked) or 'custom_date' (target date & best hourly window)
  const [muhurtaMode, setMuhurtaMode] = useState<'calendar' | 'custom_date'>('calendar');
  const [selectedTargetDate, setSelectedTargetDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  // Calculate detailed hourly slots & Tarabala on-demand for the chosen date
  const targetDateAnalysis: GemstoneTargetDateAnalysis | null = useMemo(() => {
    if (!activeReport || !selectedTargetDate) return null;
    return calculateGemstoneDateMuhurta(selectedTargetDate, activeReport.planet, kundali);
  }, [activeReport, selectedTargetDate, kundali]);

  // Quick preset dates (Today, Tomorrow, Next Prescribed Weekday)
  const datePresets = useMemo(() => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];

    const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const gemDay = GEMSTONE_DETAILS[activeReport?.planet || 'Sun']?.day || 'Sunday';
    const targetDayIdx = DAY_NAMES.findIndex(d => gemDay.toLowerCase().includes(d.toLowerCase()));
    
    let nextGemDay = new Date(today);
    let daysToAdd = targetDayIdx >= 0 ? ((targetDayIdx - today.getDay() + 7) % 7) : 1;
    if (daysToAdd === 0) daysToAdd = 7;
    nextGemDay.setDate(today.getDate() + daysToAdd);
    const nextGemDayStr = nextGemDay.toISOString().split('T')[0];

    return [
      { label: lang === 'hi' ? 'Today (Aaj)' : 'Today', date: todayStr },
      { label: lang === 'hi' ? 'Tomorrow (Kal)' : 'Tomorrow', date: tomorrowStr },
      { 
        label: lang === 'hi' ? `Next ${gemDay} (Aagami)` : `Next ${gemDay}`, 
        date: nextGemDayStr 
      }
    ];
  }, [activeReport?.planet, lang]);

  // Trigger Muhurta calculation ONLY when explicitly requested (or when the Muhurta sub-tab is opened in calendar mode)
  const calculateMuhurtaForActiveReport = useCallback((forceRefresh: boolean = false) => {
    if (!activeReport) {
      setMuhurtaWindows([]);
      return;
    }

    const moonPlanet = kundali.planets?.find(p => p.name === 'Moon');
    const moonSignIdx = moonPlanet ? Math.floor(moonPlanet.longitude / 30) : 0;
    const nakIndex = NAKSHATRAS.findIndex(n => n.name.toLowerCase() === jatakaNakshatra.toLowerCase());
    const janmaNakIdx = nakIndex >= 0 ? nakIndex : 0;
    const cacheKey = `${activeReport.planet}_${janmaNakIdx}_${moonSignIdx}`;

    if (!forceRefresh && muhurtaCache[cacheKey]) {
      setMuhurtaWindows(muhurtaCache[cacheKey]);
      setIsLoadingMuhurta(false);
      return;
    }

    setIsLoadingMuhurta(true);
    const activityId = PLANET_MUHURTA_MILESTONE_MAP[activeReport.planet] || 'ruby_manik';

    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const futureDate = new Date(today);
    futureDate.setDate(today.getDate() + 35);
    const futureStr = futureDate.toISOString().split('T')[0];

    const params: MuhurtaSearchParams = {
      activityId,
      latitude: kundali.birthDetails.latitude || 28.6139,
      longitude: kundali.birthDetails.longitude || 77.2090,
      startDate: todayStr,
      endDate: futureStr,
      janmaNakIdxs: [{ name: kundali.birthDetails.name || 'Jataka', idx: janmaNakIdx }],
      janmaRashiIdxs: [{ name: kundali.birthDetails.name || 'Jataka', idx: moonSignIdx }],
      navataraFilter: 'all_auspicious',
      maxTransits: 5
    };

    Promise.resolve(findBestMuhurtas(params))
      .then((res) => {
        const results = res || [];
        setMuhurtaCache(prev => ({ ...prev, [cacheKey]: results }));
        setMuhurtaWindows(results);
        setIsLoadingMuhurta(false);
      })
      .catch((err) => {
        console.error('Error calculating gemstone wearing Muhurta:', err);
        setMuhurtaWindows([]);
        setIsLoadingMuhurta(false);
      });
  }, [activeReport, kundali, jatakaNakshatra, muhurtaCache]);

  // Lazy computation: only calculate when user is viewing the 'muhurta' tab
  useEffect(() => {
    if (reportSubTab === 'muhurta' && activeReport) {
      calculateMuhurtaForActiveReport(false);
    }
  }, [reportSubTab, activeReport?.planet, calculateMuhurtaForActiveReport]);

  // Run a server-side AI consultation specific to the active gemstone analysis
  const handleAskGemini = async () => {
    if (!aiQuestion.trim() || !activeReport) return;
    setIsAiLoading(true);
    setAiAnswer(null);

    try {
      const payload = {
        question: aiQuestion,
        context: {
          birthDetails: kundali.birthDetails,
          objective: OBJECTIVES[selectedObjective]?.name,
          activePlanet: activeReport.planet,
          gemstone: activeReport.gemstone,
          suitability: activeReport.suitability,
          reasons: activeReport.primaryReasons,
          cautions: activeReport.cautionPoints,
          d1Dignity: activeReport.factors.d1.value,
          shadbalaCategory: activeReport.factors.shadbala.value,
          vargaAlignment: activeReport.factors.relevantVarga.description
        }
      };

      const response = await fetch('/api/gemstone-consult', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Consultation request failed');
      }

      const data = await response.json();
      setAiAnswer(data.answer);
    } catch (err) {
      console.error('Error in Gemini gemstone consult:', err);
      setAiAnswer(buildOfflineGemstoneConsultFallback(activeReport, kundali.houses[0].sign, OBJECTIVES[selectedObjective]?.name));
    } finally {
      setIsAiLoading(false);
    }
  };

  const currentObjectiveName = OBJECTIVES[selectedObjective]?.name || 'General';
  const jatakaName = kundali.birthDetails.name || 'Jataka';
  const lagnaSign = kundali.houses[0]?.sign || 'Aries';
  const moonPlanet = kundali.planets?.find(p => p.name === 'Moon');
  const moonSign = moonPlanet?.sign || 'Unknown';

  const handlePrintPrescription = () => {
    window.print();
  };

  if (!activeReport) {
    return (
      <div className="p-8 text-center bg-astro-card border border-astro-border rounded-2xl">
        <p className="text-sm text-astro-secondary-text">No gemstone candidate reports generated for this selection.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" id="gemstone-advisor-section">
      
      {/* 1. Header Banner & Intro */}
      <div className="bg-gradient-to-br from-astro-primary via-[#0B2E2C] to-[#0A2422] text-white rounded-2xl p-6 sm:p-8 shadow-md relative overflow-hidden">
        <div className="absolute right-0 top-0 translate-x-12 -translate-y-12 opacity-10">
          <Gem className="w-72 h-72 text-amber-400" />
        </div>
        <div className="max-w-3xl space-y-3 relative z-10">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-400/20 border border-amber-400/30 rounded-full text-xs text-amber-200 font-bold tracking-wider uppercase">
              <Sparkles className="w-3.5 h-3.5" /> High-Precision Vedic Jyotish Engine
            </div>
            <div className="px-2.5 py-0.5 bg-white/10 rounded-full text-[11px] text-emerald-200 border border-white/10 font-medium">
              Native: {jatakaName} • {lagnaSign} Lagna • {jatakaNakshatra} Nakshatra
            </div>
          </div>

          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-amber-100">
            Upaya & Gemstone Advisor (Ratna & Vedic Upaya)
          </h2>
          
          <p className="text-sm text-emerald-100/90 leading-relaxed">
            Evaluates your complete celestial framework—D1 dignity, Shadbala virupas, Navamsha confirmation, divisional vargas, Ashtakavarga energy levels, and transit timings—for your chosen life objective. Calculates auspicious wearing Muhurtas, complete remedial Upayas (mantras, charity, fasting, Rudraksha), and produces an exportable astrological prescription.
          </p>

          <div className="pt-2 flex flex-wrap gap-2">
            <button
              onClick={() => setShowPrescriptionModal(true)}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-900 text-xs font-bold rounded-xl flex items-center gap-2 transition-all shadow-sm cursor-pointer"
            >
              <FileText className="w-4 h-4" />
              {lang === 'hi' ? '📄 View / Print Prescription PDF' : '📄 Download / Print Prescription PDF'}
            </button>
          </div>
        </div>
      </div>

      {/* Cuspal Sensitivity Warning Banner */}
      {birthSensitivity.isSensitive && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 sm:p-5 text-xs space-y-2 text-amber-950 animate-fade-in">
          <div className="flex items-center gap-2 font-bold text-amber-900 text-sm">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            <span>High Cuspal Birth-Time Sensitivity Detected ({birthSensitivity.ascendantDegreeInSign.toFixed(2)}° of {kundali.houses[0]?.sign})</span>
          </div>
          <p className="text-amber-900/90 leading-relaxed text-xs">
            Your Ascendant is within {birthSensitivity.marginArcMinutes.toFixed(1)} arcminutes of the sign boundary. Under classical Parashari principles, a birth time shift of even 1–2 minutes could change the rising sign, altering functional lordships and gemstone suitability.
          </p>
          <ul className="list-disc list-inside text-amber-900/80 space-y-0.5 text-[11px]">
            {birthSensitivity.sensitivityFlags.map((flag, idx) => (
              <li key={idx}>{flag}</li>
            ))}
          </ul>
          <div className="text-[11px] font-semibold text-amber-950 pt-1">
            Guidance: {birthSensitivity.guidance}
          </div>
        </div>
      )}

      {/* No-Intervention Baseline Banner (when all stones are contraindicated) */}
      {noInterventionEval.isRecommended && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-4 sm:p-5 text-xs space-y-2 text-emerald-950 animate-fade-in">
          <div className="flex items-center gap-2 font-bold text-emerald-900 text-sm">
            <Shield className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{noInterventionEval.title}</span>
          </div>
          <p className="text-emerald-900/90 leading-relaxed text-xs">
            {noInterventionEval.rationale}
          </p>
          <div className="space-y-1 pt-1">
            <span className="font-bold text-[11px] uppercase tracking-wider text-emerald-900">Recommended Classical Alternatives:</span>
            <ul className="list-disc list-inside text-emerald-900/80 space-y-0.5 text-[11px]">
              {noInterventionEval.suggestedAlternatives.map((alt, idx) => (
                <li key={idx}>{alt}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Master Framework Switcher */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-2 shadow-2xs">
        <div className="flex items-center gap-1.5 overflow-x-auto text-xs font-semibold scrollbar-thin">
          <button
            onClick={() => setMasterTab('perspectives')}
            className={`px-4 py-2.5 rounded-xl transition-all flex items-center gap-2 shrink-0 cursor-pointer ${
              masterTab === 'perspectives'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'text-astro-secondary-text hover:text-astro-text hover:bg-astro-card'
            }`}
          >
            <Sparkles className="w-4 h-4 text-amber-300" />
            <span>{lang === 'hi' ? 'Classical Perspectives (1,5,9 • 4,10 • Dasha • Jaimini)' : 'Classical Frameworks (1,5,9 • 4,10 • Dasha • Jaimini)'}</span>
          </button>

          <button
            onClick={() => setMasterTab('conflict_checker')}
            className={`px-4 py-2.5 rounded-xl transition-all flex items-center gap-2 shrink-0 cursor-pointer ${
              masterTab === 'conflict_checker'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'text-astro-secondary-text hover:text-astro-text hover:bg-astro-card'
            }`}
          >
            <Shield className="w-4 h-4 text-rose-300" />
            <span>{lang === 'hi' ? 'Jewelry Conflict Checker ("What Am I Wearing?")' : 'Jewelry Conflict Checker ("What Am I Wearing?")'}</span>
          </button>

          <button
            onClick={() => setMasterTab('objectives')}
            className={`px-4 py-2.5 rounded-xl transition-all flex items-center gap-2 shrink-0 cursor-pointer ${
              masterTab === 'objectives'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'text-astro-secondary-text hover:text-astro-text hover:bg-astro-card'
            }`}
          >
            <Target className="w-4 h-4 text-teal-300" />
            <span>{lang === 'hi' ? 'Goal & Objective Matrix (Career, Marriage, etc.)' : 'Goal & Objective Matrix (Career, Marriage, etc.)'}</span>
          </button>
        </div>
      </div>

      {masterTab === 'perspectives' && (
        <VedicGemologyPerspectivesView
          kundali={kundali}
          lang={lang}
          onSelectGemstoneForMuhurta={(planet) => {
            setSelectedReportPlanet(planet);
            setMasterTab('objectives');
            setReportSubTab('muhurta');
          }}
        />
      )}

      {masterTab === 'conflict_checker' && (
        <JewelryConflictChecker kundali={kundali} lang={lang} />
      )}

      {masterTab === 'objectives' && (
        <>
          {/* 2. Selection Control Board */}
          <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
          
          {/* Left: Objectives Dropdown */}
          <div className="flex-1 space-y-1">
            <label className="text-[11px] font-bold text-astro-secondary-text uppercase tracking-wider block">
              Choose your focus / objective:
            </label>
            <div className="relative">
              <select
                value={selectedObjective}
                onChange={(e) => {
                  setSelectedObjective(e.target.value);
                  setGemstoneInMind(false);
                }}
                className="w-full bg-astro-card border border-astro-border focus:border-astro-primary rounded-xl px-4 py-2.5 text-sm text-astro-text focus:outline-none font-semibold appearance-none cursor-pointer"
              >
                {Object.values(OBJECTIVES).map(obj => (
                  <option key={obj.id} value={obj.id}>
                    🎯 {obj.name}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3.5 text-astro-secondary-text">
                <ChevronDown className="w-4 h-4" />
              </div>
            </div>
            <p className="text-[11px] text-astro-secondary-text/80 italic mt-1 px-1">
              {OBJECTIVES[selectedObjective]?.description}
            </p>
          </div>

          {/* Center: "I have a gemstone in mind" Switcher */}
          <div className="bg-astro-card border border-astro-border rounded-xl p-3.5 flex flex-col sm:flex-row items-start sm:items-center gap-3.5 shrink-0 min-w-[260px]">
            <div className="flex items-center gap-2">
              <input
                id="gemstoneInMindCheckbox"
                type="checkbox"
                checked={gemstoneInMind}
                onChange={(e) => setGemstoneInMind(e.target.checked)}
                className="w-4 h-4 rounded text-astro-primary focus:ring-astro-primary border-astro-border cursor-pointer"
              />
              <label htmlFor="gemstoneInMindCheckbox" className="text-xs font-bold text-astro-text cursor-pointer select-none">
                I have a gemstone in mind
              </label>
            </div>
            
            {gemstoneInMind && (
              <div className="relative flex-1 w-full sm:w-auto">
                <select
                  value={selectedPlanetInMind}
                  onChange={(e) => {
                    const pl = e.target.value as PlanetName;
                    setSelectedPlanetInMind(pl);
                    setSelectedReportPlanet(pl);
                  }}
                  className="w-full sm:w-40 bg-astro-bg border border-astro-border focus:border-astro-primary rounded-lg px-2.5 py-1 text-xs text-astro-text focus:outline-none font-semibold appearance-none cursor-pointer"
                >
                  <option value="Sun">Sun (Ruby / Manikya)</option>
                  <option value="Moon">Moon (Pearl / Moti)</option>
                  <option value="Mars">Mars (Coral / Moonga)</option>
                  <option value="Mercury">Mercury (Emerald / Panna)</option>
                  <option value="Jupiter">Jupiter (Yellow Sapphire / Pukhraj)</option>
                  <option value="Venus">Venus (Diamond / Heera)</option>
                  <option value="Saturn">Saturn (Blue Sapphire / Neelam)</option>
                  <option value="Rahu">Rahu (Hessonite / Gomed)</option>
                  <option value="Ketu">Ketu (Cats Eye / Lehsuniya)</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-astro-secondary-text">
                  <ChevronDown className="w-3.5 h-3.5" />
                </div>
              </div>
            )}
          </div>

          {/* Right: View Mode Toggle */}
          <div className="flex bg-[#F1ECE1] rounded-xl p-1 text-xs font-semibold border border-astro-border self-start md:self-center">
            <button
              onClick={() => setViewMode('basic')}
              className={`px-3.5 py-1.5 rounded-lg transition-all cursor-pointer ${viewMode === 'basic' ? 'bg-astro-primary text-white shadow-xs' : 'text-astro-secondary-text hover:text-astro-text'}`}
            >
              Summary Cards
            </button>
            <button
              onClick={() => setViewMode('advanced')}
              className={`px-3.5 py-1.5 rounded-lg transition-all cursor-pointer ${viewMode === 'advanced' ? 'bg-astro-primary text-white shadow-xs' : 'text-astro-secondary-text hover:text-astro-text'}`}
            >
              Advanced Matrix
            </button>
          </div>

        </div>
      </div>

      {/* 3. Comparative Gemstone Suitability Table / Candidates */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm space-y-4">
        <div className="flex items-center justify-between border-b border-astro-border pb-3">
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-astro-primary" />
            <h3 className="font-bold text-astro-primary font-serif text-base">
              Objective-Focused Gemstone Candidates ({suitabilityReports.length} Evaluated)
            </h3>
          </div>
          <span className="text-[11px] font-bold text-astro-primary bg-astro-primary/10 px-2.5 py-1 rounded-full uppercase border border-astro-primary/20">
            Objective: {currentObjectiveName}
          </span>
        </div>

        {viewMode === 'advanced' ? (
          /* High density matrix table */
          <div className="overflow-x-auto border border-astro-border rounded-xl">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-astro-card border-b border-astro-border text-astro-secondary-text uppercase tracking-wider font-semibold">
                  <th className="py-3 px-4">Planet & Gemstone</th>
                  <th className="py-3 px-3">Role for Goal</th>
                  <th className="py-3 px-3">D1 Dignity</th>
                  <th className="py-3 px-3">Shadbala</th>
                  <th className="py-3 px-3">Varga Support</th>
                  <th className="py-3 px-3 text-center">Score</th>
                  <th className="py-3 px-4 text-right">Suitability</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-astro-border/60">
                {suitabilityReports.map((report) => {
                  const bColors = getSuitabilityColor(report.suitability);
                  const isMindHighlight = gemstoneInMind && report.planet === selectedPlanetInMind;
                  const isCurrentlySelected = activeReport.planet === report.planet;

                  return (
                    <tr
                      key={report.planet}
                      onClick={() => setSelectedReportPlanet(report.planet)}
                      className={`cursor-pointer transition-colors ${isCurrentlySelected ? 'bg-astro-primary/10 font-medium' : isMindHighlight ? 'bg-amber-500/10' : 'hover:bg-astro-card/60'}`}
                    >
                      <td className="py-3.5 px-4 flex items-center gap-2.5">
                        <div className={`w-2.5 h-2.5 rounded-full ${report.suitability === 'STRONGLY SUITABLE' ? 'bg-emerald-500' : report.suitability === 'SUITABLE' ? 'bg-teal-500' : report.suitability === 'CONDITIONALLY SUITABLE' ? 'bg-amber-500' : 'bg-rose-500'}`} />
                        <div>
                          <div className="font-bold text-astro-text flex items-center gap-1.5">
                            {report.planet}
                            {isCurrentlySelected && (
                              <span className="text-[9px] bg-astro-primary text-white px-1.5 py-0.2 rounded font-mono">SELECTED</span>
                            )}
                          </div>
                          <div className="text-[11px] text-astro-secondary-text">{report.gemstone}</div>
                        </div>
                      </td>
                      <td className="py-3.5 px-3 text-astro-text max-w-[170px] truncate" title={report.functionalRole}>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded mr-1 ${report.objectiveRole === 'PRIMARY_CONTENDER' ? 'bg-emerald-100 text-emerald-800' : report.objectiveRole === 'SUPPORTING' ? 'bg-teal-100 text-teal-800' : 'bg-slate-100 text-slate-700'}`}>
                          {report.objectiveRole === 'PRIMARY_CONTENDER' ? 'PRIMARY' : report.objectiveRole === 'SUPPORTING' ? 'SUPPORT' : 'AFFLICTED'}
                        </span>
                        {report.functionalRole}
                      </td>
                      <td className="py-3.5 px-3 text-astro-secondary-text">
                        {report.factors.d1.value} <span className="text-[10px] text-astro-secondary-text">({report.factors.bhava.value})</span>
                      </td>
                      <td className="py-3.5 px-3 text-astro-secondary-text">
                        {report.factors.shadbala.value}
                      </td>
                      <td className="py-3.5 px-3">
                        <span className="text-astro-text font-semibold">{report.factors.relevantVarga.value}</span>
                      </td>
                      <td className="py-3.5 px-3 text-center">
                        <span className="font-mono font-bold bg-[#F1ECE1] px-2 py-0.5 rounded border border-astro-border">{report.overallScore}</span>
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <span className={`inline-block px-2.5 py-1 rounded-lg text-[10px] font-extrabold border ${bColors.bg}`}>
                          {report.suitability}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          /* Simplified Basic cards layout */
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {suitabilityReports.map((report) => {
              const bColors = getSuitabilityColor(report.suitability);
              const isSelected = activeReport.planet === report.planet;

              return (
                <div
                  key={report.planet}
                  onClick={() => setSelectedReportPlanet(report.planet)}
                  className={`border rounded-xl p-4 cursor-pointer transition-all flex flex-col justify-between ${isSelected ? 'border-astro-primary bg-astro-primary/5 ring-2 ring-astro-primary/20 shadow-xs' : 'border-astro-border bg-astro-card hover:border-astro-primary/50'}`}
                >
                  <div className="space-y-2">
                    <div className="flex justify-between items-start gap-1.5">
                      <div>
                        <h4 className="font-bold text-sm text-astro-text">{report.planet} ({report.gemstone})</h4>
                        <p className="text-[10px] text-astro-secondary-text font-medium">{report.functionalRole}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[9px] font-bold border shrink-0 ${bColors.bg}`}>
                        {report.suitability}
                      </span>
                    </div>
                    <p className="text-xs text-astro-secondary-text line-clamp-3 leading-relaxed">
                      {report.whyExplanation}
                    </p>
                  </div>

                  <div className="pt-3 mt-2 border-t border-astro-border/50 flex items-center justify-between text-[11px]">
                    <span className="text-astro-secondary-text">Score: <strong className="text-astro-text font-mono">{report.overallScore}</strong></span>
                    <span className={`text-[10px] font-bold ${isSelected ? 'text-astro-primary' : 'text-astro-secondary-text'}`}>
                      {isSelected ? '✓ Active View' : 'Click to Inspect →'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 3.1 Multi-Perspective Scenario Comparisons */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-xs space-y-4">
        <div className="flex items-center justify-between border-b border-astro-border pb-3">
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-astro-primary" />
            <h3 className="font-bold text-astro-primary font-serif text-base">
              Scenario Comparisons (Natal vs Dasha vs Gochar vs Baseline)
            </h3>
          </div>
          <span className="text-[11px] text-astro-secondary-text font-medium">
            4 Evaluated Astrological Epochs
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
          {scenarioComparisons.map((sc, idx) => {
            const bColors = getSuitabilityColor(sc.suitability);
            return (
              <div
                key={idx}
                className="bg-astro-bg border border-astro-border rounded-xl p-3.5 space-y-2 flex flex-col justify-between"
              >
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-astro-primary font-sans">{sc.title}</span>
                    <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded border ${bColors.bg}`}>
                      {sc.suitability}
                    </span>
                  </div>
                  <div className="text-xs font-bold text-astro-text font-serif">
                    {sc.primaryGemstone}
                  </div>
                  <p className="text-[11px] text-astro-secondary-text leading-relaxed">
                    {sc.rationale}
                  </p>
                </div>
                <div className="pt-2 border-t border-astro-border/50 flex items-center justify-between text-[10px] text-astro-secondary-text font-mono">
                  <span>Score: {sc.score}/100</span>
                  <span className="text-emerald-700 font-semibold">{sc.scenario}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 4. Deep Factor-by-Factor & Upaya Dashboard for Selected Planet */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 sm:p-6 shadow-sm space-y-6">
        
        {/* Active Gemstone Header Banner */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-astro-border pb-4">
          <div className="flex items-center gap-3.5">
            <div className="w-14 h-14 rounded-2xl bg-astro-primary text-white flex items-center justify-center font-serif text-3xl font-bold shadow-sm shrink-0">
              💎
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-serif font-bold text-2xl text-astro-primary">
                  {activeReport.gemstone}
                </h3>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-extrabold border ${getSuitabilityColor(activeReport.suitability).bg}`}>
                  {activeReport.suitability}
                </span>
              </div>
              <p className="text-xs text-astro-secondary-text font-medium mt-0.5">
                Ruling Graha: <strong className="text-astro-primary">{activeReport.planet}</strong> | Purpose: <strong>{currentObjectiveName}</strong> | Score: <span className="font-mono font-bold text-astro-text">{activeReport.overallScore}/100</span>
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 self-stretch sm:self-auto justify-end">
            <button
              onClick={() => setMasterTab('conflict_checker')}
              className="px-3 py-2 bg-astro-card border border-astro-border text-astro-text text-xs font-bold rounded-xl flex items-center gap-1.5 hover:bg-astro-bg transition-all cursor-pointer shadow-2xs"
              title="Audit combinations with your existing jewelry"
            >
              <Shield className="w-3.5 h-3.5 text-rose-500" />
              Pairing Safety
            </button>
            <button
              onClick={() => {
                const activeOverride = acharyaOverrides.find(o => o.planet === activeReport.planet);
                if (activeOverride) {
                  setOverrideSuitability(activeOverride.overriddenSuitability);
                  setOverrideScore(activeOverride.overriddenScore);
                  setOverrideRationale(activeOverride.rationale);
                  setOverrideSources(activeOverride.classicalSources.join(', '));
                } else {
                  setOverrideSuitability(activeReport.suitability);
                  setOverrideScore(activeReport.overallScore);
                  setOverrideRationale('');
                  setOverrideSources('Brihat Parashara Hora Shastra, Ch. 34');
                }
                setShowAcharyaOverrideModal(true);
              }}
              className="px-3 py-2 bg-amber-500/10 border border-amber-500/20 text-amber-700 text-xs font-bold rounded-xl flex items-center gap-1.5 hover:bg-amber-500/20 transition-all cursor-pointer shadow-2xs"
              title="Acharya Consultation Override & Audit Record"
            >
              <Award className="w-3.5 h-3.5 text-amber-600" />
              Acharya Override
            </button>
            <button
              onClick={() => setShowPrescriptionModal(true)}
              className="px-3.5 py-2 bg-astro-primary text-white text-xs font-bold rounded-xl flex items-center gap-1.5 hover:bg-astro-primary/90 transition-all cursor-pointer shadow-xs"
            >
              <Printer className="w-3.5 h-3.5" />
              Prescription Sheet
            </button>
          </div>
        </div>

        {/* Acharya Override Banner if active */}
        {acharyaOverrides.some(o => o.planet === activeReport.planet) && (
          <div className="p-3.5 bg-amber-500/10 border border-amber-500/30 rounded-xl text-xs space-y-1">
            <div className="flex items-center gap-1.5 font-bold text-amber-800">
              <Award className="w-4 h-4 text-amber-600 shrink-0" />
              <span>Acharya Consultation Override Active</span>
            </div>
            {(() => {
              const ov = acharyaOverrides.find(o => o.planet === activeReport.planet)!;
              return (
                <div className="text-[11px] text-amber-900/90 space-y-0.5">
                  <p><strong>Overridden Status:</strong> {ov.overriddenSuitability} ({ov.overriddenScore}/100 pts) • Original Engine: {ov.originalSuitability} ({ov.originalScore}/100 pts)</p>
                  <p><strong>Rationale:</strong> {ov.rationale}</p>
                  <p><strong>Classical Authority:</strong> {ov.classicalSources.join(', ')} • <em>Client Consent Verified</em></p>
                </div>
              );
            })()}
          </div>
        )}

        {/* Sub-tab Switcher for Selected Gemstone: Factors / Muhurta / Upayas */}
        <div className="flex border-b border-astro-border gap-2 text-xs font-semibold overflow-x-auto">
          <button
            onClick={() => setReportSubTab('factors')}
            className={`py-2.5 px-4 border-b-2 transition-all flex items-center gap-2 shrink-0 cursor-pointer ${
              reportSubTab === 'factors'
                ? 'border-astro-primary text-astro-primary bg-astro-primary/5'
                : 'border-transparent text-astro-secondary-text hover:text-astro-text'
            }`}
          >
            <Shield className="w-4 h-4" />
            11-Factor Astrological Audit
          </button>

          <button
            onClick={() => setReportSubTab('muhurta')}
            className={`py-2.5 px-4 border-b-2 transition-all flex items-center gap-2 shrink-0 cursor-pointer ${
              reportSubTab === 'muhurta'
                ? 'border-astro-primary text-astro-primary bg-astro-primary/5'
                : 'border-transparent text-astro-secondary-text hover:text-astro-text'
            }`}
          >
            <Clock className="w-4 h-4" />
            Shubh Wearing Muhurta ({muhurtaWindows.length} Windows)
          </button>

          <button
            onClick={() => setReportSubTab('upayas')}
            className={`py-2.5 px-4 border-b-2 transition-all flex items-center gap-2 shrink-0 cursor-pointer ${
              reportSubTab === 'upayas'
                ? 'border-astro-primary text-astro-primary bg-astro-primary/5'
                : 'border-transparent text-astro-secondary-text hover:text-astro-text'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            Vedic Upayas & Mantras Suite
          </button>
        </div>

        {/* TAB 1: 11-Factor Astrological Audit & Pros/Caution */}
        {reportSubTab === 'factors' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-in">
            
            {/* Left 2 cols: Factors & Reasoning */}
            <div className="lg:col-span-2 space-y-5">
              
              {/* Astrological Assessment Summary */}
              <div className="p-4 bg-astro-card border border-astro-border rounded-xl space-y-2">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-astro-primary bg-astro-primary/10 px-2.5 py-1 rounded border border-astro-primary/20 inline-block">
                  Astrological Assessment Summary
                </span>
                <p className="text-sm text-astro-text leading-relaxed font-medium">
                  {activeReport.whyExplanation}
                </p>
              </div>

              {/* 11 Parameters Grid */}
              <div className="space-y-3">
                <h4 className="text-xs font-extrabold uppercase tracking-wider text-astro-secondary-text">
                  Vedic Suitability Factors Checklist ({Object.keys(activeReport.factors).length} parameters evaluated)
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Object.values(activeReport.factors).map((f) => (
                    <div
                      key={f.name}
                      className="p-3.5 rounded-xl border border-astro-border bg-astro-card space-y-1 hover:border-astro-primary/30 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-astro-text">{f.name}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${f.status === 'positive' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : f.status === 'caution' ? 'bg-amber-50 text-amber-800 border-amber-200' : f.status === 'negative' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                          {f.value}
                        </span>
                      </div>
                      <p className="text-[11px] text-astro-secondary-text leading-snug">
                        {f.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Classical Source Citation */}
              <div className="pt-2 border-t border-astro-border space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-bold text-astro-accent">
                  <FileText className="w-4 h-4" />
                  <span>Classical Jyotisha Source Reference:</span>
                </div>
                <div className="text-[11px] bg-[#FFFBF0] border border-[#F2E8D5] rounded-xl p-3.5 text-astro-secondary-text space-y-1">
                  <div>
                    <span className="font-bold text-astro-accent">[Rule Base]: </span>
                    {JYOTISHA_SOURCES.functionalLordship.rule}
                  </div>
                  <div className="text-[10px] italic pt-1 flex items-center justify-between">
                    <span>Source: {JYOTISHA_SOURCES.functionalLordship.source}</span>
                    <span className="bg-astro-accent/10 text-astro-accent px-1.5 py-0.5 rounded text-[9px] font-semibold">
                      Confidence: {JYOTISHA_SOURCES.functionalLordship.confidence}
                    </span>
                  </div>
                </div>
              </div>

            </div>

            {/* Right 1 col: Pros, Cautions, and Wearing Specs */}
            <div className="space-y-5">
              
              {/* Ritual & Wear Specifications */}
              <div className="bg-astro-card border border-astro-border rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 border-b border-astro-border pb-2">
                  <Flame className="w-4 h-4 text-astro-accent" />
                  <h4 className="font-bold text-sm text-astro-text">
                    Wearing Protocol & Metal
                  </h4>
                </div>

                <div className="space-y-2.5 text-xs">
                  <div className="flex justify-between border-b border-astro-border/50 pb-1">
                    <span className="text-astro-secondary-text">Day of Wear:</span>
                    <span className="font-semibold text-astro-text">{GEMSTONE_DETAILS[activeReport.planet]?.day}</span>
                  </div>
                  <div className="flex justify-between border-b border-astro-border/50 pb-1">
                    <span className="text-astro-secondary-text">Metal Alloy:</span>
                    <span className="font-semibold text-astro-text">{GEMSTONE_DETAILS[activeReport.planet]?.metal}</span>
                  </div>
                  <div className="flex justify-between border-b border-astro-border/50 pb-1">
                    <span className="text-astro-secondary-text">Finger:</span>
                    <span className="font-semibold text-astro-text">{GEMSTONE_DETAILS[activeReport.planet]?.finger}</span>
                  </div>
                  <div className="flex justify-between border-b border-astro-border/50 pb-1">
                    <span className="text-astro-secondary-text">Substitute:</span>
                    <span className="font-semibold text-astro-accent">{GEMSTONE_DETAILS[activeReport.planet]?.substitute}</span>
                  </div>
                  <div className="flex justify-between border-b border-astro-border/50 pb-1">
                    <span className="text-astro-secondary-text">Weight Guide:</span>
                    <span className="font-semibold text-astro-text">{GEMSTONE_DETAILS[activeReport.planet]?.weight}</span>
                  </div>
                </div>
              </div>

              {/* Pros & Caution Checklist */}
              <div className="bg-astro-card border border-astro-border rounded-xl p-4 space-y-3.5">
                <div className="flex items-center gap-2 border-b border-astro-border pb-2">
                  <Shield className="w-4 h-4 text-astro-primary" />
                  <h4 className="font-bold text-sm text-astro-text">
                    Pros & Caution Checklist
                  </h4>
                </div>

                {/* Positive reasons */}
                <div className="space-y-2">
                  <div className="text-[10px] uppercase font-bold text-emerald-700 flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Auspicious Inducements
                  </div>
                  <ul className="space-y-1.5">
                    {activeReport.primaryReasons.map((reason, idx) => (
                      <li key={idx} className="flex items-start gap-1.5 text-xs text-astro-secondary-text">
                        <span className="text-emerald-600 font-bold shrink-0 mt-0.5">•</span>
                        <span>{reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Caution points */}
                <div className="space-y-2 border-t border-astro-border/60 pt-2.5">
                  <div className="text-[10px] uppercase font-bold text-amber-700 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Cautionary & Risk Factors
                  </div>
                  <ul className="space-y-1.5">
                    {activeReport.cautionPoints.map((caution, idx) => (
                      <li key={idx} className="flex items-start gap-1.5 text-xs text-astro-secondary-text">
                        <span className="text-amber-600 font-bold shrink-0 mt-0.5">•</span>
                        <span>{caution}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

            </div>

          </div>
        )}

        {/* TAB 2: Shubh Wearing Muhurta Windows */}
        {reportSubTab === 'muhurta' && (
          <div className="space-y-4 animate-fade-in">
            {/* Mode Switcher Bar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-3 bg-astro-card border border-astro-border rounded-xl">
              <div className="flex items-center gap-1 bg-[#F1ECE1] p-1 rounded-lg border border-astro-border/50">
                <button
                  type="button"
                  onClick={() => setMuhurtaMode('calendar')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                    muhurtaMode === 'calendar'
                      ? 'bg-astro-primary text-white shadow-2xs'
                      : 'text-astro-secondary-text hover:text-astro-text'
                  }`}
                >
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{lang === 'hi' ? '35-Day Best Calendar (35-Divasiya Shreshtha)' : '35-Day Best Calendar'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setMuhurtaMode('custom_date')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                    muhurtaMode === 'custom_date'
                      ? 'bg-astro-primary text-white shadow-2xs'
                      : 'text-astro-secondary-text hover:text-astro-text'
                  }`}
                >
                  <Target className="w-3.5 h-3.5" />
                  <span>{lang === 'hi' ? 'Target Date & Best Time (Vishishta Tithi)' : 'Target Date & Best Time'}</span>
                </button>
              </div>

              <div className="flex items-center justify-between sm:justify-end gap-2 shrink-0">
                {muhurtaMode === 'calendar' && (
                  <button
                    type="button"
                    onClick={() => calculateMuhurtaForActiveReport(true)}
                    disabled={isLoadingMuhurta}
                    className="px-2.5 py-1.5 bg-emerald-700/10 hover:bg-emerald-700/20 text-emerald-900 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50"
                    title="Recalculate 35-day wearing windows"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isLoadingMuhurta ? 'animate-spin' : ''}`} />
                    {lang === 'hi' ? 'Recalculate (Punarganana)' : 'Recalculate'}
                  </button>
                )}
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('astro_navigate_tab', { detail: 'muhurta' }))}
                  className="text-xs font-bold text-astro-primary hover:underline flex items-center gap-1 shrink-0"
                >
                  Full Muhurta Advisor <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* MODE A: 35-Day Ranked Calendar */}
            {muhurtaMode === 'calendar' && (
              <div className="space-y-4">
                <div className="p-3.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                  <h4 className="text-xs font-bold text-emerald-900 flex items-center gap-2">
                    <span>Auspicious Consecration Windows for {activeReport.gemstone} ({activeReport.planet})</span>
                    {isLoadingMuhurta && <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-700 inline" />}
                  </h4>
                  <p className="text-[11px] text-emerald-800/80 mt-0.5">
                    Ranked by Tarabala for {jatakaName}'s Janma Nakshatra ({jatakaNakshatra}) & Moon Sign ({moonSign}) with Shuddhi checks.
                  </p>
                </div>

                {isLoadingMuhurta ? (
                  <div className="p-10 text-center bg-astro-card border border-astro-border rounded-xl space-y-3">
                    <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mx-auto" />
                    <div className="text-xs font-bold text-astro-text">
                      {lang === 'hi' ? 'Evaluating 35-day astronomical transit windows, Tarabala & Hora...' : 'Evaluating 35-day astronomical transit windows, Tarabala & Hora...'}
                    </div>
                    <p className="text-[11px] text-astro-secondary-text">
                      Filtering {activeReport.gemstone} wearing slots matching strict Vedic guidelines.
                    </p>
                  </div>
                ) : muhurtaWindows.length === 0 ? (
                  <div className="p-6 text-center bg-astro-card border border-astro-border rounded-xl">
                    <Clock className="w-8 h-8 text-astro-secondary-text mx-auto mb-2 opacity-50" />
                    <p className="text-xs text-astro-secondary-text">No immediate auspicious transit windows found in the next 35 days matching strict Tarabala and Shuddhi constraints.</p>
                    <p className="text-[11px] text-astro-secondary-text/75 mt-1">Try selecting a custom target date or wearing during Shukla Paksha on {GEMSTONE_DETAILS[activeReport.planet]?.day}.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {muhurtaWindows.map((m, idx) => {
                      const dateObj = new Date(m.dateTime);
                      const formattedDate = dateObj.toLocaleDateString(lang === 'hi' ? 'hi-IN' : 'en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      });

                      return (
                        <div
                          key={idx}
                          className="bg-astro-card border border-astro-border rounded-xl p-4 space-y-3 hover:border-astro-primary/40 transition-all shadow-2xs"
                        >
                          <div className="flex items-start justify-between gap-2 border-b border-astro-border pb-2">
                            <div>
                              <div className="text-[10px] uppercase font-bold text-astro-primary tracking-wider">
                                Rank #{idx + 1} Shubh Slot
                              </div>
                              <div className="font-bold text-sm text-astro-text">{formattedDate}</div>
                            </div>
                            <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-700 font-bold text-xs border border-emerald-500/20">
                              {m.score.totalScore}/100
                            </span>
                          </div>

                          <div className="space-y-1.5 text-xs text-astro-secondary-text">
                            <div className="flex justify-between">
                              <span>Tithi:</span>
                              <strong className="text-astro-text">{m.panchang.tithi}</strong>
                            </div>
                            <div className="flex justify-between">
                              <span>Nakshatra:</span>
                              <strong className="text-astro-text">{m.panchang.nakshatra}</strong>
                            </div>
                            <div className="flex justify-between">
                              <span>Vara (Day):</span>
                              <strong className="text-astro-text">{m.panchang.vara}</strong>
                            </div>
                            <div className="flex justify-between">
                              <span>Tarabala:</span>
                              <strong className="text-emerald-700 font-bold">{m.taraBala?.taraName} ({m.taraBala?.suitability})</strong>
                            </div>
                            {m.panchang.abhijit?.start && (
                              <div className="flex justify-between bg-[#F1ECE1] p-1.5 rounded text-[11px] font-medium">
                                <span>Abhijit Muhurta:</span>
                                <span className="font-mono text-astro-primary">{m.panchang.abhijit.start} - {m.panchang.abhijit.end}</span>
                              </div>
                            )}
                          </div>

                          <div className="text-[10px] text-astro-secondary-text/80 italic border-t border-astro-border/50 pt-2">
                            Purify with raw milk & gangajal, chant {GEMSTONE_DETAILS[activeReport.planet]?.mantra.slice(0, 30)}... before wearing.
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* MODE B: Pick Specific Date & Best Time Finder */}
            {muhurtaMode === 'custom_date' && targetDateAnalysis && (
              <div className="space-y-4">
                {/* Date Selection & Presets */}
                <div className="p-4 bg-astro-card border border-astro-border rounded-xl space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <label htmlFor="gemstone-muhurta-target-date" className="block text-xs font-bold text-astro-text">
                        {lang === 'hi' ? 'Select Target Wearing Date (Dharana Tithi):' : 'Select Target Wearing Date:'}
                      </label>
                      <p className="text-[11px] text-astro-secondary-text">
                        {`Evaluate day suitability & find the most potent hourly time window for ${activeReport.gemstone}.`}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        id="gemstone-muhurta-target-date"
                        type="date"
                        value={selectedTargetDate}
                        onChange={(e) => setSelectedTargetDate(e.target.value)}
                        className="px-3 py-1.5 bg-[#FAF6ED] border border-astro-border rounded-lg text-xs font-bold text-astro-text focus:outline-none focus:border-astro-primary"
                      />
                    </div>
                  </div>

                  {/* Preset Shortcuts */}
                  <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-astro-border/40">
                    <span className="text-[11px] font-semibold text-astro-secondary-text">
                      {lang === 'hi' ? 'Quick Presets (Tvarita Vikalpa):' : 'Quick Presets:'}
                    </span>
                    {datePresets.map((p, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setSelectedTargetDate(p.date)}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all ${
                          selectedTargetDate === p.date
                            ? 'bg-astro-primary text-white shadow-2xs'
                            : 'bg-[#F1ECE1] hover:bg-amber-100/70 text-astro-text border border-astro-border/60'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Day Suitability Card */}
                <div className="bg-astro-card border border-astro-border rounded-xl p-4 space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-astro-border pb-3">
                    <div>
                      <div className="text-[10px] uppercase font-bold text-astro-primary tracking-wider">
                        {lang === 'hi' ? 'Selected Date Evaluation (Dainik Panchanga & Tarabala)' : 'Selected Date Evaluation'}
                      </div>
                      <div className="text-base font-bold text-astro-text">{targetDateAnalysis.formattedDate}</div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span
                        className={`px-3 py-1 rounded-lg text-xs font-bold border ${
                          targetDateAnalysis.grade === 'EXCELLENT'
                            ? 'bg-emerald-500/10 text-emerald-800 border-emerald-500/30'
                            : targetDateAnalysis.grade === 'GOOD'
                            ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20'
                            : targetDateAnalysis.grade === 'MODERATE'
                            ? 'bg-amber-500/10 text-amber-800 border-amber-500/30'
                            : 'bg-rose-500/10 text-rose-800 border-rose-500/30'
                        }`}
                      >
                        {targetDateAnalysis.overallDayScore}/100 • {targetDateAnalysis.gradeLabel}
                      </span>
                    </div>
                  </div>

                  {/* 4 Key Panchanga Metrics */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    <div className="bg-[#FAF6ED] p-2.5 rounded-lg border border-astro-border space-y-0.5">
                      <div className="text-[10px] text-astro-secondary-text">{lang === 'hi' ? 'Weekday (Vara)' : 'Weekday'}</div>
                      <div className="text-xs font-bold text-astro-text flex items-center gap-1">
                        <span>{targetDateAnalysis.vara}</span>
                        {targetDateAnalysis.isPrescribedDay ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        ) : (
                          <span className="text-[10px] text-amber-700 font-normal">({targetDateAnalysis.prescribedDayName})</span>
                        )}
                      </div>
                    </div>

                    <div className="bg-[#FAF6ED] p-2.5 rounded-lg border border-astro-border space-y-0.5">
                      <div className="text-[10px] text-astro-secondary-text">{lang === 'hi' ? 'Tithi & Paksha' : 'Tithi & Paksha'}</div>
                      <div className="text-xs font-bold text-astro-text truncate" title={targetDateAnalysis.tithi}>
                        {targetDateAnalysis.tithi}
                      </div>
                    </div>

                    <div className="bg-[#FAF6ED] p-2.5 rounded-lg border border-astro-border space-y-0.5">
                      <div className="text-[10px] text-astro-secondary-text">{lang === 'hi' ? 'Moon Nakshatra' : 'Moon Nakshatra'}</div>
                      <div className="text-xs font-bold text-astro-text truncate">
                        {targetDateAnalysis.nakshatra}
                      </div>
                    </div>

                    <div className="bg-[#FAF6ED] p-2.5 rounded-lg border border-astro-border space-y-0.5">
                      <div className="text-[10px] text-astro-secondary-text">{lang === 'hi' ? 'Tarabala (Native)' : 'Tarabala (Native)'}</div>
                      <div className={`text-xs font-bold ${targetDateAnalysis.taraBala.isAuspicious ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {targetDateAnalysis.taraBala.taraName} ({targetDateAnalysis.taraBala.suitability})
                      </div>
                    </div>
                  </div>

                  {/* Auspicious & Inauspicious Bullet Highlights */}
                  <div className="space-y-1.5 pt-1 text-xs">
                    {targetDateAnalysis.auspiciousFactors.map((f, i) => (
                      <div key={i} className="flex items-start gap-1.5 text-emerald-800">
                        <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </div>
                    ))}
                    {targetDateAnalysis.prohibitions.map((p, i) => (
                      <div key={i} className="flex items-start gap-1.5 text-rose-800">
                        <AlertTriangle className="w-3.5 h-3.5 text-rose-600 shrink-0 mt-0.5" />
                        <span>{p}</span>
                      </div>
                    ))}
                  </div>

                  {/* Nearest Auspicious Date Recommendation if Sub-optimal */}
                  {targetDateAnalysis.nearestAuspiciousDate && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <div className="text-xs text-amber-900">
                          <span className="font-bold">
                            {lang === 'hi' ? 'Recommended Alternate Date (Sujhav): ' : 'Recommended Alternate Date: '}
                          </span>
                          {targetDateAnalysis.nearestAuspiciousDate.formattedDate} — {targetDateAnalysis.nearestAuspiciousDate.reason}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedTargetDate(targetDateAnalysis.nearestAuspiciousDate!.dateStr)}
                        className="px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold shrink-0 flex items-center gap-1 transition-colors"
                      >
                        <span>{lang === 'hi' ? 'Switch to this Date' : 'Switch to this Date'}</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Best Hourly Time Window Spotlight */}
                {targetDateAnalysis.bestSlot && (
                  <div className="p-4 bg-gradient-to-r from-emerald-500/10 via-amber-500/10 to-emerald-500/10 border border-emerald-500/30 rounded-xl space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] uppercase font-bold text-emerald-900 tracking-wider flex items-center gap-1">
                        <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
                        {lang === 'hi' ? 'Best Recommended Time Window on this Day' : 'Best Recommended Time Window on this Day'}
                      </div>
                      <span className="px-2 py-0.5 rounded bg-emerald-600 text-white font-bold text-xs">
                        {targetDateAnalysis.bestSlot.score}/100
                      </span>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <div className="text-lg font-bold text-astro-text font-mono">
                          {targetDateAnalysis.bestSlot.startTime} – {targetDateAnalysis.bestSlot.endTime}
                        </div>
                        <div className="text-xs font-semibold text-astro-primary mt-0.5">
                          {targetDateAnalysis.bestSlot.title}
                        </div>
                      </div>
                    </div>

                    <p className="text-xs text-astro-secondary-text">
                      {targetDateAnalysis.bestSlot.rationale}
                    </p>
                  </div>
                )}

                {/* Diurnal Timeline / Hourly Slices */}
                <div className="bg-astro-card border border-astro-border rounded-xl p-4 space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 border-b border-astro-border pb-2">
                    <div className="text-xs font-bold text-astro-text flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-astro-primary" />
                      <span>{lang === 'hi' ? 'Complete Diurnal Windows (Sunrise to Sunset / Hora & Choghadiya)' : 'Complete Diurnal Windows (Sunrise to Sunset)'}</span>
                    </div>
                    <div className="text-[11px] text-astro-secondary-text flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-rose-500 inline-block"></span>
                        {lang === 'hi' ? 'Rahu Kaalam (Avoid / Rahu Kalam Varjita): ' : 'Rahu Kaalam (Avoid): '}
                        <strong className="font-mono text-astro-text">{targetDateAnalysis.rahuKalam.start} - {targetDateAnalysis.rahuKalam.end}</strong>
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                    {targetDateAnalysis.timeSlots.map((slot, sIdx) => {
                      const isTopSlot = targetDateAnalysis.bestSlot?.startTime === slot.startTime && targetDateAnalysis.bestSlot?.title === slot.title;

                      return (
                        <div
                          key={sIdx}
                          className={`p-3 rounded-xl border text-xs space-y-1.5 transition-all ${
                            slot.isAvoid
                              ? 'bg-rose-500/5 border-rose-500/20 text-rose-900'
                              : slot.isAuspicious || isTopSlot
                              ? 'bg-emerald-500/5 border-emerald-500/30 text-emerald-950'
                              : 'bg-[#FAF6ED] border-astro-border text-astro-text'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-mono font-bold text-[12px]">{slot.startTime} – {slot.endTime}</span>
                            <span
                              className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                slot.isAvoid
                                  ? 'bg-rose-500 text-white'
                                  : slot.isAuspicious
                                  ? 'bg-emerald-600 text-white'
                                  : 'bg-[#E5DFD3] text-astro-text'
                              }`}
                            >
                              {slot.isAvoid ? 'AVOID' : `${slot.score}/100`}
                            </span>
                          </div>

                          <div className="font-semibold text-xs truncate" title={slot.title}>
                            {slot.title}
                          </div>

                          <div className="text-[11px] text-astro-secondary-text leading-tight line-clamp-2">
                            {slot.rationale}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Ritual Preparation Reminder */}
                <div className="p-3 bg-[#FAF6ED] border border-astro-border rounded-xl text-[11px] text-astro-secondary-text flex items-start gap-2">
                  <Shield className="w-4 h-4 text-astro-primary shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold text-astro-text">
                      {lang === 'hi' ? 'Vedic Consecration Reminder (Vedic Prana-Pratishtha): ' : 'Vedic Consecration Reminder: '}
                    </span>
                    {`During the chosen window, sit facing East or North. Cleanse with raw milk and Gangajal, and recite the ${activeReport.planet} mantra before wearing on the prescribed finger.`}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB 3: Vedic Upayas Suite */}
        {reportSubTab === 'upayas' && (
          <div className="space-y-5 animate-fade-in">
            <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-center justify-between gap-3">
              <div>
                <h4 className="text-sm font-bold text-amber-900">
                  Comprehensive Vedic Remedial Protocol for {activeReport.planet}
                </h4>
                <p className="text-xs text-amber-800/80 mt-0.5">
                  Authentic Shastric Upayas to harmonize planetary vibrations without unintended side-effects.
                </p>
              </div>
              <span className="text-xs font-mono font-bold bg-amber-200/50 text-amber-900 px-2.5 py-1 rounded-lg">
                Graha: {activeReport.planet}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              
              {/* Mantra Japa Box */}
              <div className="bg-astro-card border border-astro-border rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 border-b border-astro-border pb-2">
                  <Sparkles className="w-4 h-4 text-astro-primary" />
                  <h4 className="font-bold text-sm text-astro-text">Mantra Sadhana & Japa</h4>
                </div>

                <div className="space-y-2 text-xs">
                  <div>
                    <span className="text-[10px] font-bold text-astro-secondary-text uppercase">Beeja Mantra:</span>
                    <p className="font-serif text-sm font-bold text-astro-primary bg-astro-primary/5 p-2 rounded-lg border border-astro-primary/10 mt-1">
                      {activeReport.upaya.beejaMantra}
                    </p>
                  </div>

                  <div>
                    <span className="text-[10px] font-bold text-astro-secondary-text uppercase">Vedic Mantra:</span>
                    <p className="font-serif text-xs text-astro-text bg-astro-bg p-2 rounded-lg border border-astro-border mt-1 leading-relaxed">
                      {activeReport.upaya.vedicMantra}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-1 text-[11px]">
                    <div className="bg-astro-bg p-2 rounded border border-astro-border">
                      <span className="text-astro-secondary-text block">Recommended Counts:</span>
                      <strong className="text-astro-text">{activeReport.upaya.japCount}</strong>
                    </div>
                    <div className="bg-astro-bg p-2 rounded border border-astro-border">
                      <span className="text-astro-secondary-text block">Rosary (Mala):</span>
                      <strong className="text-astro-text">{activeReport.upaya.mala}</strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* Daana (Charity) Box */}
              <div className="bg-astro-card border border-astro-border rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 border-b border-astro-border pb-2">
                  <HeartHandshake className="w-4 h-4 text-astro-accent" />
                  <h4 className="font-bold text-sm text-astro-text">Classical Daana (Charity)</h4>
                </div>

                <div className="space-y-2.5 text-xs">
                  <div>
                    <span className="text-[10px] font-bold text-astro-secondary-text uppercase">Prescribed Donation Items:</span>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {activeReport.upaya.daana.items.map((item, i) => (
                        <span key={i} className="px-2 py-1 bg-astro-bg border border-astro-border rounded text-[11px] font-medium text-astro-text">
                          • {item}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-1 text-[11px]">
                    <div className="bg-astro-bg p-2 rounded border border-astro-border">
                      <span className="text-astro-secondary-text block">Auspicious Day & Time:</span>
                      <strong className="text-astro-text">{activeReport.upaya.daana.day}, {activeReport.upaya.daana.time}</strong>
                    </div>
                    <div className="bg-astro-bg p-2 rounded border border-astro-border">
                      <span className="text-astro-secondary-text block">Suitable Recipient:</span>
                      <strong className="text-astro-text">{activeReport.upaya.daana.recipient}</strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rudraksha & Yantra Box */}
              <div className="bg-astro-card border border-astro-border rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 border-b border-astro-border pb-2">
                  <Shield className="w-4 h-4 text-emerald-600" />
                  <h4 className="font-bold text-sm text-astro-text">Rudraksha & Sacred Yantra</h4>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="bg-emerald-500/5 p-2.5 rounded-lg border border-emerald-500/20">
                    <span className="text-[10px] font-bold text-emerald-800 uppercase block">Prescribed Rudraksha:</span>
                    <strong className="text-sm text-emerald-900">{activeReport.upaya.rudraksha.mukhi}</strong>
                    <p className="text-[11px] text-emerald-800/80 mt-0.5">{activeReport.upaya.rudraksha.benefits}</p>
                  </div>

                  <div className="bg-astro-bg p-2.5 rounded-lg border border-astro-border">
                    <span className="text-[10px] font-bold text-astro-secondary-text uppercase block">Sacred Yantra:</span>
                    <strong className="text-xs text-astro-text">{activeReport.upaya.yantra}</strong>
                  </div>
                </div>
              </div>

              {/* Vrata & Lifestyle Karma Box */}
              <div className="bg-astro-card border border-astro-border rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 border-b border-astro-border pb-2">
                  <Flame className="w-4 h-4 text-amber-600" />
                  <h4 className="font-bold text-sm text-astro-text">Vrata (Fasting) & Lifestyle Karma</h4>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="bg-astro-bg p-2.5 rounded-lg border border-astro-border">
                    <span className="text-[10px] font-bold text-astro-secondary-text uppercase block">Vrata Method:</span>
                    <strong className="text-xs text-astro-primary">{activeReport.upaya.vrata.day} (Deity: {activeReport.upaya.vrata.deity})</strong>
                    <p className="text-[11px] text-astro-secondary-text mt-0.5">{activeReport.upaya.vrata.method}</p>
                  </div>

                  <div>
                    <span className="text-[10px] font-bold text-astro-secondary-text uppercase block mb-1">Recommended Karmic Habits:</span>
                    <ul className="space-y-1">
                      {activeReport.upaya.lifestyle.map((habit, i) => (
                        <li key={i} className="text-[11px] text-astro-secondary-text flex items-start gap-1.5">
                          <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                          <span>{habit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

            </div>
          </div>
        )}

      </div>

      {/* 5. Interactive AI Chat Consultation for Gemstone */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm space-y-4">
        <div className="flex items-center gap-2 border-b border-astro-border pb-2.5">
          <Sparkles className="w-5 h-5 text-astro-primary" />
          <h4 className="font-bold font-serif text-base text-astro-primary">
            Ask Astrologer AI regarding {activeReport.gemstone}
          </h4>
        </div>

        <p className="text-xs text-astro-secondary-text leading-relaxed">
          Inquire about specific purification rituals, compatibility with existing rings, or how this gem acts with your Mahadasha lord.
        </p>

        <div className="flex gap-2">
          <input
            type="text"
            placeholder={`Ask Gemini: e.g. "Can I wear my ${activeReport.gemstone} with silver, or should I do Rudrabhishekam first?"`}
            value={aiQuestion}
            onChange={(e) => setAiQuestion(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAskGemini()}
            className="flex-1 bg-astro-card border border-astro-border focus:border-astro-primary rounded-xl px-3.5 py-2 text-xs text-astro-text focus:outline-none font-medium"
          />
          <button
            type="button"
            onClick={handleAskGemini}
            disabled={isAiLoading || !aiQuestion.trim()}
            className="px-4 py-2 bg-astro-primary text-white text-xs font-bold rounded-xl flex items-center gap-1.5 hover:bg-astro-primary/90 disabled:opacity-40 transition-colors cursor-pointer"
          >
            {isAiLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Send className="w-3.5 h-3.5" /> Send
              </>
            )}
          </button>
        </div>

        {aiAnswer && (
          <div className="p-4 bg-emerald-50/40 border border-emerald-200/50 rounded-xl text-xs text-astro-text leading-relaxed whitespace-pre-wrap animate-fade-in font-serif">
            {aiAnswer}
          </div>
        )}
      </div>

      {/* 5.1 Quality vs Affordability Guidance */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-xs space-y-3">
        <div className="flex items-center gap-2 text-astro-primary font-serif font-bold text-base border-b border-astro-border pb-2.5">
          <Award className="w-5 h-5 text-amber-500" />
          <h4>Astrological Gemstone Quality & Affordability Principles</h4>
        </div>
        <p className="text-xs text-astro-secondary-text leading-relaxed">
          Vedic gemology relies on uninhibited optical transmission of planetary rays. Commercial gemstone pricing often favors artificial heat treatments, glass fillings, and synthetic clarity over natural resonance.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs pt-1">
          {QUALITY_AFFORDABILITY_GUIDANCE.principles.map((principle, idx) => (
            <div key={idx} className="bg-astro-bg p-3 rounded-xl border border-astro-border space-y-1">
              <div className="font-bold text-astro-primary text-[11px]">Principle {idx + 1}</div>
              <p className="text-[11px] text-astro-secondary-text leading-normal">{principle}</p>
            </div>
          ))}
        </div>
        <div className="text-[10px] text-astro-secondary-text/80 italic pt-1">
          {QUALITY_AFFORDABILITY_GUIDANCE.disclaimer}
        </div>
      </div>

      {/* 5.2 Option Comparison: Candidates & Authentic Substitutes (REQ-17) */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-xs space-y-3">
        <div className="flex items-center justify-between border-b border-astro-border pb-2.5">
          <div className="flex items-center gap-2 text-astro-primary font-serif font-bold text-base">
            <Gem className="w-5 h-5 text-emerald-600" />
            <h4>Comparative Analysis: Candidate Gemstones & Authentic Uparatnas</h4>
          </div>
          <span className="text-[11px] text-astro-secondary-text">Objective: <strong>{currentObjectiveName}</strong></span>
        </div>
        <p className="text-xs text-astro-secondary-text leading-relaxed">
          Comparison across prospective Graha gemstones and authentic classical secondary substitutes (Upratnas) under Anukul Ratna Shastra. Rankings reflect planetary resonance with your chosen life focus without false precision.
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-astro-border text-[11px] text-astro-secondary-text uppercase tracking-wider bg-astro-bg/50">
                <th className="py-2.5 px-3">Graha & Primary Stone</th>
                <th className="py-2.5 px-3">Authentic Substitutes (Uparatna)</th>
                <th className="py-2.5 px-3">Functional Role</th>
                <th className="py-2.5 px-3">Suitability Tier</th>
                <th className="py-2.5 px-3">Key Strengths</th>
                <th className="py-2.5 px-3">Cautions / Risks</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-astro-border/50">
              {optionComparisons.slice(0, 5).map((opt) => (
                <tr key={opt.planet} className="hover:bg-astro-bg/40 transition-colors">
                  <td className="py-3 px-3 font-semibold text-astro-text">
                    <div>{opt.primaryGemstone}</div>
                    <div className="text-[10px] text-astro-secondary-text font-normal">{opt.planet}</div>
                  </td>
                  <td className="py-3 px-3 text-astro-secondary-text text-[11px]">
                    {opt.substitutes.length > 0 ? opt.substitutes.join(', ') : 'Direct Primary Only'}
                  </td>
                  <td className="py-3 px-3 text-astro-secondary-text text-[11px]">
                    {opt.functionalRole}
                  </td>
                  <td className="py-3 px-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                      opt.suitability === 'STRONGLY SUITABLE' ? 'bg-emerald-100 text-emerald-800' :
                      opt.suitability === 'SUITABLE' ? 'bg-teal-100 text-teal-800' :
                      opt.suitability === 'CONDITIONALLY SUITABLE' ? 'bg-amber-100 text-amber-800' :
                      opt.suitability === 'CAUTION' ? 'bg-rose-100 text-rose-800' : 'bg-slate-100 text-slate-700'
                    }`}>
                      {opt.suitability} ({opt.overallScore} pts)
                    </span>
                  </td>
                  <td className="py-3 px-3 text-emerald-700 text-[11px]">
                    {opt.pros.length > 0 ? opt.pros[0] : 'Standard positive alignment'}
                  </td>
                  <td className="py-3 px-3 text-rose-700 text-[11px]">
                    {opt.cons.length > 0 ? opt.cons[0] : 'None recorded'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 6. Universal Classical Non-Guarantee & Purushartha Disclaimer */}
      <div className="p-4 bg-amber-500/10 border border-amber-500/20 text-amber-200 rounded-2xl space-y-1">
        <div className="flex items-center gap-1.5 text-xs font-bold text-amber-300">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>Classical Jyotisha & Purushartha Principle (Shastriya Purushartha Siddhanta)</span>
        </div>
        <p className="text-[11px] leading-relaxed text-amber-200/90">
          Gemstones (Ratna) and Upayas serve to harmonize subtle planetary frequencies (Daiva) in accordance with classical Parashari Anukul Ratna Shastra. Astrological remedies do not guarantee predetermined outcomes or override individual karma, personal effort (Purushartha), and ethical action. Recommendations must be accompanied by practical prudence and diligent self-effort.
        </p>
      </div>

      {/* Strict Medical Disclaimer if Health is the Objective */}
      {selectedObjective === 'health' && (
        <div className="p-4 bg-[#FFF1F2] border border-[#FFE4E6] text-[#9F1239] rounded-2xl space-y-1">
          <div className="flex items-center gap-1.5 text-xs font-bold">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>Strict Medical Disclaimer</span>
          </div>
          <p className="text-[10px] leading-relaxed">
            Traditional Jyotisha interpretations are provided for historical, educational, and spiritual purposes only. Gemstone wear does NOT replace professional medical diagnosis, advice, surgery, or pharmacological treatment. Consult a licensed physician for any medical concerns.
          </p>
        </div>
      )}
        </>
      )}

      {/* 7. Printable Astrological Prescription Modal */}
      {showPrescriptionModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
          <div className="bg-white text-slate-900 rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto shadow-2xl border border-slate-200 p-6 sm:p-8 space-y-6 relative font-serif">
            
            {/* Modal Controls (Hidden when printing) */}
            <div className="flex items-center justify-between border-b border-slate-200 pb-3 print:hidden">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 px-2.5 py-1 rounded">
                  Jyotisha Prescription Dossier
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrintPrescription}
                  className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 transition-all shadow-xs cursor-pointer"
                >
                  <Printer className="w-3.5 h-3.5" /> Print / Save as PDF
                </button>
                <button
                  onClick={() => setShowPrescriptionModal(false)}
                  className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* PRINTABLE DOSSIER CONTENT */}
            <div className="space-y-6 text-slate-800" id="printable-prescription">
              
              {/* Prescription Header */}
              <div className="text-center border-b-2 border-slate-800 pb-4 space-y-1">
                <div className="flex items-center justify-between text-[11px] text-slate-500 font-mono pb-1">
                  <span>Dossier ID: <strong>{printableReport?.reportId || 'VJR-001'}</strong></span>
                  <span>Engine: <strong>{GEMSTONE_ENGINE_VERSION}</strong></span>
                </div>
                <div className="text-xs uppercase font-sans tracking-widest text-slate-500 font-bold">
                  Vedic Jyotish Ratna & Upaya Paramarsha-Patra
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                  Vedic Gemstone & Upaya Prescription
                </h1>
                <p className="text-xs italic text-slate-600">
                  Calculated under Classical Parashari & Jaimini Shastras with Real-Time Ephemeris • Timestamp: {printableReport?.timestamp ? new Date(printableReport.timestamp).toLocaleString() : new Date().toLocaleString()}
                </p>
              </div>

              {/* Jataka Demographic Card */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200 text-xs font-sans">
                <div>
                  <span className="text-slate-500 block text-[10px] uppercase font-bold">Native Name</span>
                  <strong className="text-slate-900 text-sm">{jatakaName}</strong>
                </div>
                <div>
                  <span className="text-slate-500 block text-[10px] uppercase font-bold">Birth Date & Time</span>
                  <strong className="text-slate-900">{kundali.birthDetails.date} • {kundali.birthDetails.time}</strong>
                </div>
                <div>
                  <span className="text-slate-500 block text-[10px] uppercase font-bold">Lagna & Moon Sign</span>
                  <strong className="text-slate-900">{lagnaSign} Lagna / {moonSign} Rashi</strong>
                </div>
                <div>
                  <span className="text-slate-500 block text-[10px] uppercase font-bold">Nakshatra</span>
                  <strong className="text-slate-900">{jatakaNakshatra}</strong>
                </div>
              </div>

              {/* Prescribed Gemstone Summary */}
              <div className="border-2 border-emerald-800 rounded-xl p-5 space-y-3 bg-emerald-50/30">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-emerald-200 pb-2">
                  <div>
                    <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider font-sans">
                      Primary Prescribed Gemstone
                    </span>
                    <h2 className="text-2xl font-bold text-emerald-950 font-serif">
                      {activeReport.gemstone} ({activeReport.planet})
                    </h2>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] font-bold uppercase text-slate-500 font-sans block">Focus Goal</span>
                    <strong className="text-sm text-slate-800">{currentObjectiveName}</strong>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs font-sans">
                  <div>
                    <span className="text-slate-500 block">Recommended Metal:</span>
                    <strong>{GEMSTONE_DETAILS[activeReport.planet]?.metal}</strong>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Wearing Finger:</span>
                    <strong>{GEMSTONE_DETAILS[activeReport.planet]?.finger}</strong>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Weight Range:</span>
                    <strong>{GEMSTONE_DETAILS[activeReport.planet]?.weight}</strong>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Day & Time:</span>
                    <strong>{GEMSTONE_DETAILS[activeReport.planet]?.day} Morning</strong>
                  </div>
                </div>

                <div className="pt-2 border-t border-emerald-100 text-xs space-y-1.5">
                  <div>
                    <span className="font-bold text-emerald-900">Astrological Recommendation Justification: </span>
                    <span className="text-slate-700">{activeReport.whyExplanation}</span>
                  </div>
                  {printableReport?.primaryRecommendation.substitutes && printableReport.primaryRecommendation.substitutes.length > 0 && (
                    <div className="text-[11px] text-slate-600">
                      <strong className="text-slate-800">Authentic Secondary Substitutes (Uparatna): </strong>
                      <span>{printableReport.primaryRecommendation.substitutes.join(', ')}</span>
                    </div>
                  )}
                  {printableReport?.conflictingEvidence && printableReport.conflictingEvidence.length > 0 && (
                    <div className="text-[11px] text-rose-800 bg-rose-50 p-2 rounded border border-rose-200">
                      <strong>Cautionary Risk Factors: </strong>
                      <span>{printableReport.conflictingEvidence.join('; ')}</span>
                    </div>
                  )}
                  {acharyaOverrides.some(o => o.planet === activeReport.planet) && (
                    <div className="text-[11px] text-amber-800 bg-amber-50 p-2 rounded border border-amber-200">
                      <strong>Acharya Override Endorsement: </strong>
                      <span>Adjusted by {acharyaOverrides.find(o => o.planet === activeReport.planet)!.acharyaName} to {acharyaOverrides.find(o => o.planet === activeReport.planet)!.overriddenSuitability} ({acharyaOverrides.find(o => o.planet === activeReport.planet)!.overriddenScore}/100 pts) based on {acharyaOverrides.find(o => o.planet === activeReport.planet)!.classicalSources.join(', ')}.</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Shubh Muhurta Dates for Wearing */}
              <div className="space-y-2">
                <h3 className="text-sm font-bold text-slate-900 uppercase font-sans tracking-wide">
                  1. Calculated Auspicious Wearing Muhurtas (Shubha Muhurta)
                </h3>
                {muhurtaWindows.length === 0 ? (
                  <p className="text-xs text-slate-600 italic">Wear during Shukla Paksha on {GEMSTONE_DETAILS[activeReport.planet]?.day} in Abhijit Muhurta.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs font-sans">
                    {muhurtaWindows.slice(0, 3).map((m, i) => (
                      <div key={i} className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-1">
                        <div className="font-bold text-slate-900">{new Date(m.dateTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
                        <div className="text-[11px] text-slate-600">Tithi: {m.panchang.tithi}</div>
                        <div className="text-[11px] text-slate-600">Nakshatra: {m.panchang.nakshatra}</div>
                        <div className="text-[11px] font-bold text-emerald-700">Tarabala: {m.taraBala?.taraName} ({m.score.totalScore} pts)</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Vedic Upayas & Mantras */}
              <div className="space-y-3">
                <h3 className="text-sm font-bold text-slate-900 uppercase font-sans tracking-wide">
                  2. Prescribed Vedic Upayas & Mantras (Sampurna Vedic Upaya)
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-1.5">
                    <strong className="text-slate-900 font-sans block text-[11px] uppercase">Mantra Procedure</strong>
                    <p className="italic text-emerald-900 font-serif">"{activeReport.upaya.beejaMantra}"</p>
                    <p className="text-[11px] text-slate-600 font-sans">Chant {activeReport.upaya.japCount} using {activeReport.upaya.mala}.</p>
                  </div>

                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-1.5">
                    <strong className="text-slate-900 font-sans block text-[11px] uppercase">Charitable Daana</strong>
                    <p className="text-[11px] text-slate-700 font-sans">Donate: {activeReport.upaya.daana.items.join(', ')}</p>
                    <p className="text-[11px] text-slate-600 font-sans">On {activeReport.upaya.daana.day} to {activeReport.upaya.daana.recipient}.</p>
                  </div>

                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-1.5">
                    <strong className="text-slate-900 font-sans block text-[11px] uppercase">Rudraksha & Yantra</strong>
                    <p className="text-[11px] text-slate-700 font-sans">Rudraksha: <strong>{activeReport.upaya.rudraksha.mukhi}</strong></p>
                    <p className="text-[11px] text-slate-600 font-sans">Yantra: {activeReport.upaya.yantra}</p>
                  </div>

                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-1.5">
                    <strong className="text-slate-900 font-sans block text-[11px] uppercase">Vrata & Fasting</strong>
                    <p className="text-[11px] text-slate-700 font-sans">{activeReport.upaya.vrata.day} (Deity: {activeReport.upaya.vrata.deity})</p>
                    <p className="text-[11px] text-slate-600 font-sans">{activeReport.upaya.vrata.method}</p>
                  </div>
                </div>
              </div>

              {/* Astrological Seal & Disclaimer */}
              <div className="pt-4 border-t border-slate-300 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-sans text-slate-500">
                <div>
                  <p className="text-[10px] italic">Prescription generated by VedangJyotisham Classical Jyotisha Advisor Engine</p>
                  <p className="text-[10px]">Auspiciousness certified under Classical Jyotisha standards. Remedies harmonize subtle vibrations and do not guarantee material outcomes; personal effort (Purushartha) remains essential.</p>
                </div>
                <div className="text-center sm:text-right border-t sm:border-t-0 sm:border-l border-slate-200 pt-2 sm:pt-0 sm:pl-4">
                  <div className="font-bold text-slate-800 text-xs">Jyotisha Acharya Verification</div>
                  <div className="text-[10px] text-emerald-800 font-semibold font-mono">SEAL: VEDIC-UPAYA-AUTHENTIC</div>
                </div>
              </div>

            </div>

          </div>
        </div>
      )}

      {/* 8. Acharya Override Modal (REQ-18) */}
      {showAcharyaOverrideModal && activeReport && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
          <div className="bg-astro-card border border-astro-border text-astro-text rounded-2xl max-w-xl w-full shadow-2xl p-6 space-y-5 animate-fade-in">
            <div className="flex items-center justify-between border-b border-astro-border pb-3">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                <h3 className="font-serif font-bold text-lg text-astro-text">Acharya Consultation Override</h3>
              </div>
              <button
                onClick={() => setShowAcharyaOverrideModal(false)}
                className="p-1 text-astro-secondary-text hover:text-astro-text rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-astro-bg p-3.5 rounded-xl border border-astro-border text-xs space-y-1">
              <div className="font-bold text-astro-primary">{activeReport.gemstone} ({activeReport.planet})</div>
              <div className="text-astro-secondary-text">
                Original Engine Evaluation: <strong className="text-astro-text">{activeReport.suitability}</strong> ({activeReport.overallScore}/100 pts)
              </div>
              <div className="text-[11px] text-astro-secondary-text/80 italic">
                Acharya overrides record professional consultations with full provenance. The engine baseline is preserved immutably.
              </div>
            </div>

            <div className="space-y-4 text-xs">
              {activeReport && (activeReport.objectiveRole === 'CONTRAINDICATED' || activeReport.suitability === 'NOT RECOMMENDED' || (activeReport.cautionPoints && activeReport.cautionPoints.some(c => /pure dusthana|naidhana|critical tara bala/i.test(c)))) && (
                <div className="p-2.5 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-300 text-[11px] leading-relaxed">
                  <strong>[PROJECT_INTERPRETATION] [RULE_CLASSICAL_LORDSHIP: BPHS Ch. 34]:</strong> {activeReport.planet} ({activeReport.gemstone}) is a functional malefic (lordship per BPHS Ch. 34) or is placed in Naidhana Tara, and is contraindicated under project Anukul-vada gemstone policy. Overriding these project contraindications to SUITABLE or STRONGLY SUITABLE is prohibited by policy.
                </div>
              )}

              <div>
                <label className="block font-bold text-astro-secondary-text uppercase text-[11px] mb-1">
                  Overridden Suitability Tier
                </label>
                <select
                  value={overrideSuitability}
                  onChange={(e) => setOverrideSuitability(e.target.value as SuitabilityLevel)}
                  className="w-full bg-astro-bg border border-astro-border rounded-xl px-3 py-2 text-astro-text font-semibold focus:outline-none focus:border-astro-primary cursor-pointer"
                >
                  <option
                    value="STRONGLY SUITABLE"
                    disabled={activeReport?.objectiveRole === 'CONTRAINDICATED' || activeReport?.suitability === 'NOT RECOMMENDED'}
                  >
                    STRONGLY SUITABLE {activeReport?.objectiveRole === 'CONTRAINDICATED' ? '(Prohibited: Contraindicated)' : ''}
                  </option>
                  <option
                    value="SUITABLE"
                    disabled={activeReport?.objectiveRole === 'CONTRAINDICATED' || activeReport?.suitability === 'NOT RECOMMENDED'}
                  >
                    SUITABLE {activeReport?.objectiveRole === 'CONTRAINDICATED' ? '(Prohibited: Contraindicated)' : ''}
                  </option>
                  <option value="CONDITIONALLY SUITABLE">CONDITIONALLY SUITABLE</option>
                  <option value="CAUTION">CAUTION</option>
                  <option value="NOT RECOMMENDED">NOT RECOMMENDED</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-astro-secondary-text uppercase text-[11px] mb-1">
                  Adjusted Score (0 to 100)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={overrideScore}
                  onChange={(e) => setOverrideScore(Math.max(0, Math.min(100, Number(e.target.value))))}
                  className="w-full bg-astro-bg border border-astro-border rounded-xl px-3 py-2 text-astro-text font-mono font-bold focus:outline-none focus:border-astro-primary"
                />
              </div>

              <div>
                <label className="block font-bold text-astro-secondary-text uppercase text-[11px] mb-1">
                  Consultation Rationale & Shastric Rationale (Mandatory)
                </label>
                <textarea
                  rows={3}
                  value={overrideRationale}
                  onChange={(e) => setOverrideRationale(e.target.value)}
                  placeholder="Explain why this adjustment is prescribed, citing native's specific chart afflictions or remedial counter-balances..."
                  className="w-full bg-astro-bg border border-astro-border rounded-xl p-3 text-astro-text focus:outline-none focus:border-astro-primary leading-relaxed"
                />
              </div>

              <div>
                <label className="block font-bold text-astro-secondary-text uppercase text-[11px] mb-1">
                  Classical Treatise Sources
                </label>
                <input
                  type="text"
                  value={overrideSources}
                  onChange={(e) => setOverrideSources(e.target.value)}
                  placeholder="e.g. BPHS Ch. 34 (Lordship); Garuda Purana (Ratna Pariksha)"
                  className="w-full bg-astro-bg border border-astro-border rounded-xl px-3 py-2 text-astro-text focus:outline-none focus:border-astro-primary"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-astro-border">
              <button
                onClick={() => setShowAcharyaOverrideModal(false)}
                className="px-4 py-2 border border-astro-border text-astro-secondary-text hover:text-astro-text rounded-xl font-bold text-xs cursor-pointer"
              >
                Cancel
              </button>
              <button
                disabled={
                  !overrideRationale.trim() ||
                  ((activeReport?.objectiveRole === 'CONTRAINDICATED' || activeReport?.suitability === 'NOT RECOMMENDED') &&
                    (overrideSuitability === 'STRONGLY SUITABLE' || overrideSuitability === 'SUITABLE'))
                }
                onClick={() => {
                  try {
                    const record = createAcharyaOverride(activeReport, {
                      acharyaId: 'acharya_consultant_01',
                      acharyaName: 'Acharya Consultation Council',
                      overriddenSuitability: overrideSuitability,
                      overriddenScore: overrideScore,
                      rationale: overrideRationale.trim(),
                      classicalSources: overrideSources.split(';').map(s => s.trim()).filter(Boolean),
                      clientConsentVerified: true
                    });
                    setAcharyaOverrides(prev => [...prev.filter(o => o.planet !== activeReport.planet), record]);
                    setShowAcharyaOverrideModal(false);
                  } catch (err: any) {
                    alert(err.message || 'Override rejected by Shastric guardrail');
                  }
                }}
                className="px-4 py-2 bg-astro-primary hover:bg-astro-primary/90 text-white rounded-xl font-bold text-xs transition-all cursor-pointer disabled:opacity-50"
              >
                Save & Endorse Override
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 9. Feedback & Governance Modal (REQ-19) */}
      {showFeedbackModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
          <div className="bg-astro-card border border-astro-border text-astro-text rounded-2xl max-w-lg w-full shadow-2xl p-6 space-y-4 animate-fade-in">
            <div className="flex items-center justify-between border-b border-astro-border pb-3">
              <div className="flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-astro-accent" />
                <h3 className="font-serif font-bold text-lg text-astro-text">Gemstone Advisor Feedback</h3>
              </div>
              <button
                onClick={() => {
                  setShowFeedbackModal(false);
                  setFeedbackSubmitted(false);
                }}
                className="p-1 text-astro-secondary-text hover:text-astro-text rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {feedbackSubmitted ? (
              <div className="text-center py-6 space-y-2">
                <div className="w-12 h-12 bg-emerald-100 text-emerald-800 rounded-full flex items-center justify-center mx-auto">
                  <Check className="w-6 h-6" />
                </div>
                <h4 className="font-bold text-sm text-astro-text">Feedback Recorded Successfully</h4>
                <p className="text-xs text-astro-secondary-text">
                  Thank you! Your feedback is catalogued under engine version <strong className="font-mono">{GEMSTONE_ENGINE_VERSION}</strong> for governance review.
                </p>
                <button
                  onClick={() => {
                    setShowFeedbackModal(false);
                    setFeedbackSubmitted(false);
                  }}
                  className="mt-3 px-4 py-2 bg-astro-primary text-white rounded-xl text-xs font-bold cursor-pointer"
                >
                  Close
                </button>
              </div>
            ) : (
              <div className="space-y-4 text-xs">
                <p className="text-astro-secondary-text leading-relaxed">
                  Help improve the Classical Jyotisha Advisor engine. Feedback is tied strictly to engine version <strong className="font-mono">{GEMSTONE_ENGINE_VERSION}</strong>. Under our governance invariants, user feedback never mutates rules dynamically without formal review and regression tests.
                </p>

                <div>
                  <label className="block font-bold text-astro-secondary-text uppercase text-[11px] mb-1">
                    Feedback Category
                  </label>
                  <select
                    value={feedbackCategory}
                    onChange={(e) => setFeedbackCategory(e.target.value as any)}
                    className="w-full bg-astro-bg border border-astro-border rounded-xl px-3 py-2 text-astro-text font-semibold focus:outline-none focus:border-astro-primary cursor-pointer"
                  >
                    <option value="ACCURACY">Calculation & Recommendation Accuracy</option>
                    <option value="MUHURTA_TIMING">Muhurta & Timing Guidance</option>
                    <option value="REMEDIAL_EFFECT">Remedial Upayas & Mantras</option>
                    <option value="SHASTRA_REFERENCE">Classical Treatise Reference</option>
                    <option value="OTHER">Other Technical / UX Feedback</option>
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-astro-secondary-text uppercase text-[11px] mb-1">
                    Rating (1 to 5 Stars)
                  </label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFeedbackRating(star as any)}
                        className={`w-8 h-8 rounded-lg font-bold text-xs cursor-pointer transition-all ${
                          feedbackRating >= star
                            ? 'bg-amber-500 text-white shadow-xs'
                            : 'bg-astro-bg text-astro-secondary-text border border-astro-border'
                        }`}
                      >
                        {star}★
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block font-bold text-astro-secondary-text uppercase text-[11px] mb-1">
                    Comments or Observations
                  </label>
                  <textarea
                    rows={3}
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="Describe your feedback, specific chart observations, or Shastric questions..."
                    className="w-full bg-astro-bg border border-astro-border rounded-xl p-3 text-astro-text focus:outline-none focus:border-astro-primary leading-relaxed"
                  />
                </div>

                <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-astro-border">
                  <button
                    onClick={() => setShowFeedbackModal(false)}
                    className="px-4 py-2 border border-astro-border text-astro-secondary-text hover:text-astro-text rounded-xl font-bold text-xs cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      submitGemstoneFeedback({
                        planet: activeReport?.planet || 'Sun',
                        gemstone: activeReport?.gemstone || 'Ruby',
                        category: feedbackCategory,
                        rating: feedbackRating,
                        feedbackText: feedbackText.trim(),
                        userType: 'SEEKER'
                      });
                      setFeedbackSubmitted(true);
                      setFeedbackText('');
                    }}
                    className="px-4 py-2 bg-astro-primary hover:bg-astro-primary/90 text-white rounded-xl font-bold text-xs transition-all cursor-pointer"
                  >
                    Submit Feedback
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};
